import {
  DEFAULT_UPSTREAM_PROXY_PORT,
  MAX_PROXY_PORT,
  MIN_PROXY_PORT,
} from '../../constants/proxy-port.constants';
import type { UpstreamProxyConfig } from '../../electron/proxy-types';

export function isValidProxyPort(port: number | undefined): port is number {
  if (port === undefined || !Number.isInteger(port)) {
    return false;
  }

  return port >= MIN_PROXY_PORT && port <= MAX_PROXY_PORT;
}

export function getProxyPortValidationError(
  port: number | undefined,
): string | null {
  if (isValidProxyPort(port)) {
    return null;
  }

  return `Port must be an integer between ${MIN_PROXY_PORT} and ${MAX_PROXY_PORT}`;
}

export function coerceProxyPort({
  port,
  fallback,
}: {
  port: number | undefined;
  fallback: number;
}): number {
  return isValidProxyPort(port) ? port : fallback;
}

export function assertValidProxyPort(port: number | undefined): void {
  const error = getProxyPortValidationError(port);

  if (error) {
    throw new Error(error);
  }
}

export function getProxySettingsPortValidationError({
  port,
  httpsPort,
  enableHttps,
  upstreamEnabled,
  upstreamPort,
}: {
  port: number;
  httpsPort: number;
  enableHttps: boolean;
  upstreamEnabled: boolean;
  upstreamPort?: number;
}): string | null {
  return (
    getProxyPortValidationError(port) ??
    (enableHttps ? getProxyPortValidationError(httpsPort) : null) ??
    (upstreamEnabled ? getProxyPortValidationError(upstreamPort) : null)
  );
}

export function buildUpstreamProxyFromSettings({
  upstreamEnabled,
  upstreamHost,
  upstreamPort,
  upstreamAuth,
}: {
  upstreamEnabled: boolean;
  upstreamHost?: string;
  upstreamPort?: number;
  upstreamAuth?: {
    username: string;
    password: string;
  };
}): UpstreamProxyConfig {
  if (!upstreamEnabled || !upstreamHost) {
    return { enabled: false, host: '', port: 8080 };
  }

  const port = upstreamPort ?? DEFAULT_UPSTREAM_PROXY_PORT;

  return {
    enabled: true,
    host: upstreamHost,
    port,
    auth: upstreamAuth?.username ? upstreamAuth : undefined,
  };
}

export function formatUpstreamProxyForIpc({
  upstreamEnabled,
  upstreamHost,
  upstreamPort,
}: {
  upstreamEnabled: boolean;
  upstreamHost?: string;
  upstreamPort?: number;
}): string | undefined {
  const upstreamProxy = buildUpstreamProxyFromSettings({
    upstreamEnabled,
    upstreamHost,
    upstreamPort,
  });

  if (!upstreamProxy.enabled) {
    return undefined;
  }

  return `${upstreamProxy.host}:${upstreamProxy.port}`;
}
